/**
 * Utility functions for search functionality
 */

/**
 * Gets a nested value from an object using dot notation
 * @param {Object} obj - Object to get value from
 * @param {string} path - Dot notation path (e.g., 'user.role.name')
 * @returns {string} The value at the path, or empty string if not found
 */
const getNestedValue = (obj, path) => {
  return path.split('.').reduce((current, key) => {
    return (current && current[key]) ? current[key] : '';
  }, obj) || '';
};

/**
 * Filters users based on search term
 * @param {Array} users - Array of user objects
 * @param {string} searchTerm - Search term to filter by
 * @returns {Array} Filtered array of users
 */
export const filterUsers = (users, searchTerm) => {
  if (!searchTerm || !searchTerm.trim()) {
    return users || [];
  }

  const term = searchTerm.toLowerCase().trim();
  
  return (users || []).filter(user => {
    // Search in first name, last name, email, and role
    const firstName = (user.first_name || '').toLowerCase();
    const lastName = (user.last_name || '').toLowerCase();
    const email = (user.email || '').toLowerCase();
    
    return firstName.includes(term) ||
           lastName.includes(term) ||
           email.includes(term) ||
           (`${firstName} ${lastName}`).includes(term) ||
           (`${lastName} ${firstName}`).includes(term);
  });
};

/**
 * Filters projects based on search term
 * @param {Array} projects - Array of project objects
 * @param {string} searchTerm - Search term to filter by
 * @returns {Array} Filtered array of projects
 */
export const filterProjects = (projects, searchTerm) => {
  if (!searchTerm || !searchTerm.trim()) {
    return projects || [];
  }

  const term = searchTerm.toLowerCase().trim();
  
  return (projects || []).filter(project => {
    // Search in project name, description, project type, and project manager
    const name = (project.name || '').toLowerCase();
    const description = (project.description || '').toLowerCase();
    const projectType = ((project.project_type && project.project_type.name) || '').toLowerCase();
    
    // Project manager search
    let projectManagerName = '';
    if (project.project_manager) {
      const pm = project.project_manager;
      const firstName = (pm.first_name || '').toLowerCase();
      const lastName = (pm.last_name || '').toLowerCase();
      const fullName = (pm.name || '').toLowerCase();
      projectManagerName = fullName || `${firstName} ${lastName}`.trim();
    }
    
    return name.includes(term) ||
           description.includes(term) ||
           projectType.includes(term) ||
           projectManagerName.includes(term);
  });
};

/**
 * Generic filter function for any object with searchable fields
 * @param {Array} items - Array of objects to filter
 * @param {string} searchTerm - Search term to filter by
 * @param {Array} searchFields - Array of field names to search in
 * @returns {Array} Filtered array of items
 */
export const filterItems = (items, searchTerm, searchFields = []) => {
  if (!searchTerm || !searchTerm.trim()) {
    return items || [];
  }

  const term = searchTerm.toLowerCase().trim();
  
  return (items || []).filter(item => {
    return searchFields.some(field => {
      const value = getNestedValue(item, field);
      return value && value.toLowerCase().includes(term);
    });
  });
};



// WEBPACK FOOTER //
// ./src/components/SearchBar/utils/searchUtils.js